import React from 'react';
import rcss from '../../../../Styles/reports.module.css';
import { observer } from "mobx-react-lite";
import { enhancedContentSourceLocation } from "../../../../lib/ContentSource";
import { InteractiveRequestId, InteractiveRequestIdBaseProps } from "../../InteractiveRequestId";
import { ContentSourceSearchResult, EventSearchResult } from "../../../../lib/searchNetworkEvent";
import { CausationGraphObserver } from '../../../../lib/Analysis/CausationGraphObserver';
import { HasCallStackResolution } from '../../../../lib/Causation/CallStackResolution';
import { CausationGraphView } from '../../../../lib/Causation/CausationGraphView';
import { GetsDbEventsByIndex } from '../../../../State/EvidenceDB/DbEventCache';
import { Literal } from '../../../../Styles/ReportStyles';
import { HasLicensePermissions } from '../../../../State/EvidenceDB/LicensePermissions';
import { UnlicensedText } from '../../../../constants';

const maxCharsPerLine = 108;


export const ContextLineView = ({
	searchResult,
	// hideDetails = false,
}: {
	searchResult: ContentSourceSearchResult;
	hideDetails?: boolean;
})=> {
	const { stringFound, encodingSteps } = searchResult;
	let { prefix, suffix } = searchResult;
	const totalChars = prefix.length + stringFound.length + suffix.length;
	if (totalChars > maxCharsPerLine) {
		const charCountAvailableForPrefixAndSuffix = Math.max(0, maxCharsPerLine - stringFound.length);
		const suffixCharCount = Math.min(
			// Suffix char count can't have more characters than the suffix
			suffix.length,
			Math.max(
				// The number of characters left after the prefix is removed
				charCountAvailableForPrefixAndSuffix - prefix.length,
				// The equal(ish) share of available characters
				Math.floor(charCountAvailableForPrefixAndSuffix / 2)
			)
		);
		const prefixCharCount = Math.min(prefix.length, charCountAvailableForPrefixAndSuffix - suffixCharCount);
		suffix = suffix.slice(0, suffixCharCount);
		prefix = prefix.slice(prefix.length - prefixCharCount);
	}
	return prefix.length === 0 && suffix.length === 0 && encodingSteps.length === 0 ? null : (
		<div className={rcss.contextLine}>
			<span className={rcss.contextSpan}>{prefix}</span>
			<span className={rcss.emphasizedResultSpan}>{stringFound}</span>
			<span className={rcss.contextSpan}>{suffix}</span>
		</div>
	);
}

export const SearchResultDetailsView = observer( ({
	result,
	causationGraphObserver,
	callStackResolution,
	getsDbEventsByIndex,
	licensePermissions,
	...rest
}: InteractiveRequestIdBaseProps & HasCallStackResolution & HasLicensePermissions & {
	result: EventSearchResult;
	getsDbEventsByIndex: GetsDbEventsByIndex;
	causationGraphObserver?: CausationGraphObserver;
}) => {
	const hideDetails = !licensePermissions.mayGenerateDetailedEvidenceAt({pageUrl: result.originUrl});
	const hideCausationGraph = !licensePermissions.mayGenerateCausationGraphAt({pageUrl: result.originUrl});
	const { eventIndex, requestId, searchResult, originUrl } = result;
	const licenseRestrictionText = !licensePermissions.mayUseForCommercialPurposesAt({pageUrl: originUrl}) ?
			UnlicensedText : undefined;
	const { position } = searchResult;
	const enhancedLocation = enhancedContentSourceLocation(searchResult);
	const cause = causationGraphObserver == null ? undefined : // "no observer" :
		eventIndex == null ? undefined : // "no index" :
		causationGraphObserver.getCauseOfEventIndex(eventIndex);
	return (
		<div className={rcss.indented} data-license={licenseRestrictionText}>
			<div className={rcss.searchLocationLine}>
				<Literal>{enhancedLocation}{ hideDetails ? '' : `:${position}`}</Literal>
				&nbsp;in request&nbsp;
				{ hideDetails ? `${requestId}` : (
					<InteractiveRequestId {...{requestId, eventIndex, ...rest}} />
				)}
			</div>
			{ hideDetails ? null : (
				<ContextLineView {...{searchResult}} />
			)}
			{ (cause == null || hideCausationGraph) ? null : (
				<CausationGraphView nodeCaused={cause} {...{callStackResolution, getsDbEventsByIndex}} />
			)}
		</div>
	);
});


